import { Dropdown, Flex, Select } from 'antd';
import { Header } from 'antd/es/layout/layout';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import type { TUserRoles } from '../../app/app.constants';

import {
  ChevronDownIcon,
  CircleXIcon,
  GripVerticalIcon,
  More01Icon,
  UsersIcon,
} from '@/icons';
import IMAGES from '../../assets/images';
import CustomImage from '../../components/CustomImage';
import CustomNavigationItem from '../../components/CustomNavItem';
import CustomNavigationItemSm from '../../components/CustomNavItemSm';

import useAuth from '../../hooks/useAuth';
import useModuleStatus from '../../hooks/useModuleStatus';
import { cn } from '../../lib/cn';
import { APP_ROUTES } from '../../routes/routes';
import { filterRoutesByRole, findMenuKeys } from '../../utils';

import ProfileMenu from './ProfileMenu';

const Navbar = () => {
  const { role } = useAuth();
  const { isEnabled } = useModuleStatus();
  const location = useLocation();

  const [showAll, setShowAll] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);

  const contentRef = useRef<HTMLDivElement>(null);

  const filteredMenu = filterRoutesByRole(
    APP_ROUTES,
    role as TUserRoles,
    isEnabled,
    true,
  );

  const { selectedKey, openKeys } = findMenuKeys(
    filteredMenu,
    location.pathname,
  );

  const navbarMenu = filteredMenu?.find((menu) => menu.path === openKeys?.[0]);
  const defaultMenu = navbarMenu?.children?.filter((menu) => !menu.showOnMore);
  const moreMenu = navbarMenu?.children?.filter((menu) => menu.showOnMore);

  const navMenuSm = useMemo(() => {
    const children = navbarMenu?.children || [];
    return showAll ? children : children.slice(0, 3);
  }, [showAll, navbarMenu]);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [showAll, navMenuSm]);

  const isActiveMore = () => {
    return moreMenu?.some((menu) => menu.path === selectedKey);
  };
  return (
    <>
      {/* ========== TOP NAVBAR ========== */}
      <Header
        className={cn(
          '!px-4 flex items-center justify-between gap-2 h-[100px]',
          'bg-theme-slate-800 rounded-b-xl',
          'md:bg-transparent md:rounded-b-none',
        )}
      >
        {/* Desktop Navigation */}
        <div
          className={cn(
            'hidden md:flex items-center gap-4 p-2 my-[10px]',
            'overflow-x-auto',
            'dark:bg-theme-slate-800 border dark:border-theme-gray-700',
            'rounded-xl',
          )}
        >
          {(defaultMenu || []).map(({ path, label, icon: Icon }) => (
            <CustomNavigationItem
              key={path}
              path={path}
              label={label as string}
              isActive={selectedKey === path}
              icon={Icon ? <Icon className="text-xl stroke-1" /> : null}
              classNames={{
                button:
                  ' w-auto px-3 py-4 min-h-[56px] h-10 flex-row rounded-xl text-nowrap !text-base',
                icon: 'rounded-xl shrink-0',
              }}
            />
          ))}

          {moreMenu && moreMenu.length > 0 && (
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              popupRender={() => (
                <div className="p-3 space-y-3 bg-theme-slate-800 border border-theme-gray-700 rounded-xl mt-3 -mr-2 w-[350px]">
                  {moreMenu.map(({ path, label, icon: Icon }) => (
                    <CustomNavigationItem
                      key={path}
                      path={path}
                      label={label as string}
                      isActive={selectedKey === path}
                      icon={
                        Icon ? (
                          <Icon className="text-xl" strokeWidth={1.6} />
                        ) : null
                      }
                      classNames={{
                        button:
                          'w-full px-3 py-4 min-h-[56px] h-10 flex-row justify-start rounded-xl text-nowrap !text-base',
                        icon: 'rounded-xl shrink-0',
                      }}
                    />
                  ))}
                </div>
              )}
            >
              <button>
                <CustomNavigationItem
                  label={'More'}
                  isActive={isActiveMore()}
                  icon={<More01Icon className="text-xl" strokeWidth={1.6} />}
                  classNames={{
                    button:
                      'w-auto px-3 py-4 min-h-[56px] h-10 flex-row rounded-xl text-nowrap !text-base',
                    icon: 'rounded-xl shrink-0',
                  }}
                />
              </button>
            </Dropdown>
          )}
        </div>

        {/* Mobile Logo */}
        <Link to="/admin/employee/dashboard" className="md:hidden">
          <Flex align="center">
            <CustomImage
              alt="HRX"
              height={53}
              width={112}
              preview={false}
              src={IMAGES.hrx}
            />
          </Flex>
        </Link>

        {/* Mobile User Selector */}
        <Select
          suffixIcon={<ChevronDownIcon />}
          className="w-[70px] shrink-0 md:!hidden"
          value="user"
          options={[
            {
              value: 'user',
              label: (
                <Flex
                  align="center"
                  justify="center"
                  className={cn(
                    'size-[31px] rounded-full bg-theme-slate-700',
                    'transition-all duration-200',
                  )}
                >
                  <UsersIcon className="text-2xl" strokeWidth={1.6} />
                </Flex>
              ),
            },
          ]}
        />

        {/* Actions */}
        <Flex align="center" className="gap-x-2 md:gap-4 shrink-0">
          {/* <Button
            icon={<SettingsIcon className="text-2xl" />}
            className="border-none shadow-none !text-white hover:!text-white bg-theme-slate-900 rounded-full"
          />
          <Button
            icon={<BellIcon className="text-2xl" />}
            className="border-none shadow-none !text-white hover:!text-white bg-theme-slate-900 rounded-full"
          /> */}
          <ProfileMenu />
        </Flex>
      </Header>

      {/* ========== MOBILE BOTTOM NAV (Expanding) ========== */}
      <motion.div
        animate={{ height: showAll ? contentHeight : 92 }}
        transition={{ duration: 0.35, ease: 'easeInOut' }}
        style={{ overflow: 'hidden' }}
        className={cn(
          'fixed bottom-0 left-0 right-0 z-[1111] md:hidden',
          'bg-theme-slate-800 rounded-t-xl',
        )}
      >
        <div
          ref={contentRef}
          className="flex flex-wrap gap-y-5 justify-between p-5"
        >
          {navMenuSm.map(({ path, label, icon: Icon }) => (
            <CustomNavigationItemSm
              key={path}
              path={path}
              label={label as string}
              isActive={selectedKey === path}
              icon={
                Icon ? <Icon className="text-xl" strokeWidth={1.6} /> : null
              }
            />
          ))}

          <CustomNavigationItemSm
            label={showAll ? 'Close' : 'More'}
            onClick={() => setShowAll(!showAll)}
            isActive={false}
            icon={
              showAll ? (
                <CircleXIcon className="text-xl" strokeWidth={1.6} />
              ) : (
                <GripVerticalIcon className="text-xl" strokeWidth={1.6} />
              )
            }
          />
        </div>
      </motion.div>
    </>
  );
};

export default Navbar;
